import FormErrorMessage from '@components/FormErrorMessage';
import PlacingOrderView from '@components/PlacingOrderView';
import Radio from '@components/Radio';
import WalletIcon from '@assets/icons/wallet.svg';
import WalletPinkIcon from '@assets/icons/wallet-pink-16.svg';
import { ButtonType } from '@components/Button';
import { formatSumFromObject, formatSumWithCurrency } from '@utils/formatValues';
import { IResaleProcessingModalWindowProps } from '../types';
import { Loader } from '@components/Loader';
import { Text, TextColor, TextSize } from '@components/Text';
import { TPlacingOrderViewData } from '@components/PlacingOrderView/types';
import { useMemo, useState } from 'react';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from 'react-i18next';

const ResaleProcessingModalWindow: React.FC<IResaleProcessingModalWindowProps> = (props: IResaleProcessingModalWindowProps): React.ReactElement => {
  const {
    errorSendResaleOrder,
    errorSendTopUpBalance,
    flow,
    handlerOnCloseResaleProcessing,
    handlerSubmit,
    isErrorSendResaleOrder,
    isErrorSendTopUpBalance,
    isLoadingSendResaleOrder,
    isLoadingSendTopUpBalance,
    parcel,
    parcelsAny,
    parcelsGood,
    resaleOrderInProcessed,
    selectedResaleCost,
    sumCny,
    sumRub
  } = props;

  const { account } = useProfile();
  const { t } = useTranslation();

  const variableCurrency: string = account?.region === 'TR' ? '$' : '¥';

  const availableOrders = parcel?.availableForResaleOrders ?? [];
  const [selectedOrderParcelId, setSelectedOrderParcelId] = useState<string>(
      availableOrders[0]?.id ?? ''
  );
  const selectedOrder = availableOrders.find(o => o.id === selectedOrderParcelId);

  const placingOrderViewData: TPlacingOrderViewData = useMemo(() => {
    const condition: boolean = account!.balance < selectedResaleCost;
    return {
      notice: {
        title: t('resaleOrder.warningTitle')
      },
      points: [
        {
          cols: [
            {
              title: t('resaleOrder.pointListItem1'),
              value: formatSumWithCurrency(selectedResaleCost, variableCurrency)
            },
            {
              color: condition ? TextColor.WARM_PINK : TextColor.PRIMARY,
              icon: condition ? WalletPinkIcon : WalletIcon,
              title: t('resaleOrder.pointListItem2'),
              value: formatSumWithCurrency(account!.balance, variableCurrency)
            },
            ...(condition
                ? [
                  {
                    title: t('resaleOrder.pointListItem3'),
                    value: formatSumWithCurrency(Math.round((selectedResaleCost - account!.balance) * 100) / 100, variableCurrency)
                  }
                ]
                : [])
          ],
          title: t('resaleOrder.point1')
        },
        {
          cols: [
            {
              title: t('resaleOrder.pointListItem4'),
              value: parcelsGood!.length
            },
            {
              title: t('resaleOrder.pointListItem5'),
              value: [formatSumWithCurrency(sumRub, '₽'), formatSumWithCurrency(sumCny, variableCurrency)]
            }
          ]
        }
      ],
      tabs: [
        {
          title: t('resaleOrder.infoTab1'),
          quantity: parcelsGood!.length
        },
        ...(parcelsAny
            ? [
              {
                title: t('resaleOrder.infoTab2'),
                quantity: parcelsAny.length
              }
            ]
            : [])
      ],
      title: t('resaleOrder.title')
    };
  }, [parcelsAny, parcelsGood, selectedResaleCost]);

  return (
      <>
        {resaleOrderInProcessed ? (
            <Loader
                alignment="center"
                size="xl"
            />
        ) : (
            <>
              {parcel?.attachments && parcel.attachments.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <Text size={TextSize.M} color={TextColor.PRIMARY}>
                      <b>{t('resaleProcessingModalWindow.willBeResold')}</b>
                    </Text>
                    <div style={{ marginTop: 12, border: '1px solid #E6E8EB', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, padding: '8px 12px', background: '#F5F6F8' }}>
                        <Text size={TextSize.XS} color={TextColor.DARK_MUTED}>{t('resaleProcessingModalWindow.itemName')}</Text>
                        <Text size={TextSize.XS} color={TextColor.DARK_MUTED}>{t('resaleProcessingModalWindow.itemPrice')}</Text>
                      </div>
                      {parcel.attachments.map((item, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, padding: '10px 12px', borderTop: idx === 0 ? 'none' : '1px solid #E6E8EB' }}>
                            <a href={item.url} target="_blank" rel="noreferrer" style={{ color: '#1577F0', textDecoration: 'none' }}>
                              <Text size={TextSize.S} color={TextColor.PRIMARY}>{item.name}</Text>
                            </a>
                            <Text size={TextSize.S} color={TextColor.PRIMARY}>{formatSumFromObject(item.price)}</Text>
                          </div>
                      ))}
                    </div>
                  </div>
              )}

              {availableOrders.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <Text size={TextSize.M} color={TextColor.PRIMARY}>
                      <b>{t('resaleProcessingModalWindow.toWhichParcel')}</b>
                    </Text>
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {availableOrders.map((order) => {
                        const isSelected = order.id === selectedOrderParcelId;
                        const isSingle = availableOrders.length === 1;
                        return (
                            <div
                                key={order.id}
                                onClick={() => !isSingle && setSelectedOrderParcelId(order.id)}
                                style={{
                                  border: `1px solid ${isSelected ? '#1577F0' : '#E6E8EB'}`,
                                  borderRadius: 8,
                                  padding: '12px',
                                  cursor: isSingle ? 'default' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12
                                }}
                            >
                              {!isSingle && (
                                  <Radio
                                      id={`resale-order-${order.id}`}
                                      checked={isSelected}
                                      onChange={() => setSelectedOrderParcelId(order.id)}
                                  />
                              )}
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                <Text size={TextSize.S} color={TextColor.DARK_MUTED}>
                                  {t('resaleProcessingModalWindow.trackNumber')}:
                                </Text>
                                <Text size={TextSize.S} color={TextColor.PRIMARY}>
                                  <b>{order.trackNumber}</b>
                                </Text>
                              </div>
                            </div>
                        );
                      })}
                    </div>
                  </div>
              )}

              {selectedOrder && selectedOrder.excessItems.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <Text size={TextSize.M} color={TextColor.PRIMARY}>
                      <b>{t('resaleProcessingModalWindow.alsoIncluded')}</b>
                    </Text>
                    <div style={{ marginTop: 12, border: '1px solid #FFD9A8', borderRadius: 8, overflow: 'hidden', background: '#FFF8EE' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px', gap: 12, padding: '8px 12px', borderBottom: '1px solid #FFD9A8' }}>
                        <Text size={TextSize.XS} color={TextColor.DARK_MUTED}>{t('resaleProcessingModalWindow.itemName')}</Text>
                        <Text size={TextSize.XS} color={TextColor.DARK_MUTED}>{t('resaleProcessingModalWindow.itemQuantity')}</Text>
                        <Text size={TextSize.XS} color={TextColor.DARK_MUTED}>{t('resaleProcessingModalWindow.itemPrice')}</Text>
                      </div>
                      {selectedOrder.excessItems.map((item, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 120px', gap: 12, padding: '10px 12px', borderTop: idx === 0 ? 'none' : '1px solid #FFD9A8' }}>
                            <a href={item.url} target="_blank" rel="noreferrer" style={{ color: '#1577F0', textDecoration: 'none' }}>
                              <Text size={TextSize.S} color={TextColor.PRIMARY}>{item.name}</Text>
                            </a>
                            <Text size={TextSize.S} color={TextColor.PRIMARY}>{item.quantity}</Text>
                            <Text size={TextSize.S} color={TextColor.PRIMARY}>{formatSumFromObject(item.price)}</Text>
                          </div>
                      ))}
                    </div>
                  </div>
              )}

              <PlacingOrderView
                  buttons={[
                    {
                      action: handlerOnCloseResaleProcessing,
                      disabled: isLoadingSendResaleOrder || isLoadingSendTopUpBalance,
                      text: t('resaleProcessingModalWindow.cancel'),
                      type: ButtonType.SKELETON
                    }, {
                      action: () => handlerSubmit(selectedOrderParcelId),
                      disabled: isLoadingSendResaleOrder
                          || isLoadingSendTopUpBalance
                          || (flow === 'resale' && !selectedOrderParcelId),
                      text: t('resaleProcessingModalWindow.payForResale'),
                      type: ButtonType.BLOCK
                    }
                  ]}
                  parcelsAny={parcelsAny}
                  parcelsGood={parcelsGood}
                  placingOrderViewData={placingOrderViewData}
              />
            </>
        )}
        {isErrorSendResaleOrder && <FormErrorMessage error={errorSendResaleOrder}/>}
        {isErrorSendTopUpBalance && <FormErrorMessage error={errorSendTopUpBalance}/>}
      </>
  );
};

export default ResaleProcessingModalWindow;
